import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { HERO_SLIDES } from "@/lib/data";

export default function HeroCarousel() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((i) => (i + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <section data-testid="hero-section" className="relative h-[calc(100vh-5rem)] min-h-[600px] overflow-hidden bg-black">
      {HERO_SLIDES.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === active ? "opacity-100" : "opacity-0"}`}
        >
          <img src={s.image} alt="" className={`absolute inset-0 w-full h-full object-cover ${i === active ? "animate-kenburns" : ""}`} />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30 lg:to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        </div>
      ))}

      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-center pt-24 pb-16 lg:justify-end lg:pt-28 lg:pb-24">
        <div className="max-w-3xl">
          <p
            key={`eb-${active}`}
            data-testid="hero-eyebrow"
            className="animate-slide-right text-[#ffb700] text-xs sm:text-sm uppercase tracking-[0.3em] font-semibold mb-6"
          >
            ◆ {HERO_SLIDES[active].eyebrow}
          </p>
          <h1
            key={`t-${active}`}
            data-testid="hero-title"
            className="animate-fade-up font-display text-5xl sm:text-7xl lg:text-[6rem] xl:text-[8rem] leading-[0.9] tracking-tight text-white text-balance"
          >
            {HERO_SLIDES[active].title.split(" ").map((w, idx) => (
              <span key={idx} className={idx === 1 ? "phoenix-text" : ""}>
                {w}{" "}
              </span>
            ))}
          </h1>
          <p
            key={`s-${active}`}
            data-testid="hero-subtitle"
            className="animate-fade-up text-base sm:text-lg lg:text-xl text-white/75 mt-6 max-w-2xl leading-relaxed"
            style={{ animationDelay: "0.1s" }}
          >
            {HERO_SLIDES[active].subtitle}
          </p>

          <div className="mt-10 flex flex-wrap gap-3 sm:gap-4">
            <Link to="/book-trial">
              <button
                data-testid="hero-book-trial-btn"
                className="btn-phoenix bg-[#ff4500] text-white px-8 h-14 uppercase tracking-wider text-sm font-semibold inline-flex items-center gap-2"
              >
                Book Free Trial Class <ChevronRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/classes">
              <button
                data-testid="hero-explore-programs-btn"
                className="border border-white/30 text-white px-8 h-14 uppercase tracking-wider text-sm font-semibold hover:bg-white hover:text-black transition-colors"
              >
                Explore Programs
              </button>
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 right-8 z-10 hidden md:flex items-center gap-2">
        <button
          data-testid="hero-prev"
          onClick={() => setActive((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)}
          className="w-12 h-12 flex items-center justify-center border border-white/20 text-white hover:bg-[#ff4500] hover:border-[#ff4500] transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-white/60 text-sm tabular-nums px-3">
          0{active + 1} <span className="text-white/30">/ 0{HERO_SLIDES.length}</span>
        </span>
        <button
          data-testid="hero-next"
          onClick={() => setActive((i) => (i + 1) % HERO_SLIDES.length)}
          className="w-12 h-12 flex items-center justify-center border border-white/20 text-white hover:bg-[#ff4500] hover:border-[#ff4500] transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </section>
  );
}
